import { ethers } from "ethers"
import { useCallback } from "react"
import { useFerroBoostContract, useTokenContract } from "src/hooks/useContract"
import { parseUnits } from "ethers/lib/utils"
import { XFER, XFER_GAS_PRICE, XFER_GAS_LIMIT } from "src/constants"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { TransactionSummaryData } from "src/components/TransactionSummary"

const useFerroBoostHandlers = () => {
  const ferroBoost = useFerroBoostContract()
  const erc20 = useTokenContract(XFER)
  const { addTransactions } = useTransactionModal()

  const handleApprove = useCallback(async () => {
    if (ferroBoost && erc20) {
      const txn = async () => {
        return await erc20.approve(
          ferroBoost.address,
          ethers.constants.MaxUint256,
          {
            gasPrice: XFER_GAS_PRICE,
          },
        )
      }
      const summary = [{ label: "Approve", amount: "", token: XFER }]

      await addTransactions([txn], summary)
    }
  }, [addTransactions, erc20, ferroBoost])

  const handleDeposit = useCallback(
    async (pid: string, amount: string, summary: TransactionSummaryData) => {
      if (ferroBoost) {
        const txn = async () => {
          return await ferroBoost.deposit(
            pid,
            parseUnits(amount, XFER.decimals),
            {
              gasPrice: XFER_GAS_PRICE,
              gasLimit: XFER_GAS_LIMIT,
            },
          )
        }
        await addTransactions([txn], summary)
      }
    },
    [addTransactions, ferroBoost],
  )

  const handleBatchWithdraw = useCallback(
    async (stakeIds: number[], summary: TransactionSummaryData) => {
      if (ferroBoost) {
        const txn = async () => {
          return await ferroBoost.batchWithdraw(
            stakeIds.map((x) => x.toString()),
            {
              gasPrice: XFER_GAS_PRICE,
              gasLimit: XFER_GAS_LIMIT,
            },
          )
        }

        await addTransactions([txn], summary)
      }
    },
    [addTransactions, ferroBoost],
  )

  const handleBatchUpgrade = useCallback(
    async (
      stakeIds: number[],
      newPids: number[],
      summary: TransactionSummaryData,
    ) => {
      if (ferroBoost) {
        const txn = async () => {
          return await ferroBoost.batchUpgrade(
            stakeIds.map((x) => x.toString()),
            newPids.map((x) => x.toString()),
            {
              gasPrice: XFER_GAS_PRICE,
              gasLimit: XFER_GAS_LIMIT,
            },
          )
        }
        await addTransactions([txn], summary)
      }
    },
    [addTransactions, ferroBoost],
  )

  return {
    onApprove: handleApprove,
    onDeposit: handleDeposit,
    onBatchWithdraw: handleBatchWithdraw,
    onBatchUpgrade: handleBatchUpgrade,
  }
}

export default useFerroBoostHandlers
